import React, { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { toast } from 'sonner';

import { useSendFeedback } from '@/api/generated';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Rating } from '@/components/ui/rating';
import {
  type FeedbackSchema,
  feedbackSchema,
} from '@/routes/(app)/(threads)/lib/schema';

interface GiveFeedbackDialogProps {
  messageId: string;
  threadId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const GiveFeedbackDialog = ({
  messageId,
  threadId,
  open,
  onOpenChange,
}: GiveFeedbackDialogProps) => {
  const { t } = useTranslation();
  const form = useForm<FeedbackSchema>({
    resolver: zodResolver(feedbackSchema),
  });
  const { mutate: sendFeedback, isPending } = useSendFeedback();

  const onSubmit = useCallback(
    (data: FeedbackSchema) => {
      sendFeedback(
        {
          messageId,
          threadId,
          data: {
            score: data.score,
            feedback: data.feedback,
          },
        },
        {
          onSuccess: () => {
            onOpenChange(false);
            form.reset();
            toast.success(t('threads.giveFeedbackDialog.success'));
          },
          onError: () => {
            toast.error(t('threads.giveFeedbackDialog.error'));
          },
        }
      );
    },
    [form, messageId, onOpenChange, sendFeedback, t, threadId]
  );

  const handleCancel = useCallback(() => {
    onOpenChange(false);
    form.reset();
  }, [form, onOpenChange]);

  const handleOpenChange = useCallback(
    (_open: boolean) => {
      onOpenChange(_open);
      form.reset();
    },
    [form, onOpenChange]
  );

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('threads.giveFeedbackDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('threads.giveFeedbackDialog.description')}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className='flex flex-col gap-4'>
            <FormField
              control={form.control}
              name='score'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('threads.giveFeedbackDialog.score')}</FormLabel>
                  <FormControl>
                    <Rating
                      {...field}
                      onChange={field.onChange}
                      score={field.value}>
                      <Rating.Item
                        score={1}
                        className='text-success hover:text-success-foreground data-[selected=true]:text-success-foreground data-[selected=true]:fill-transparent'
                        selected={field.value === 1}>
                        <ThumbsUp />
                      </Rating.Item>
                      <Rating.Item
                        score={0}
                        className='text-destructive hover:text-destructive-foreground data-[selected=true]:text-destructive-foreground data-[selected=true]:fill-transparent'
                        selected={field.value === 0}>
                        <ThumbsDown />
                      </Rating.Item>
                    </Rating>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='feedback'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('threads.giveFeedbackDialog.feedback')}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                variant='outline'
                onClick={handleCancel}>
                {t('threads.giveFeedbackDialog.cancel')}
              </Button>
              <Button
                type='submit'
                disabled={isPending}>
                {t('threads.giveFeedbackDialog.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
